import { Avatar, Box, IconButton, Stack, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import React from "react";
import theme from "Theme/AppTheme";
import { AcxMainTextFieldRef } from "components/UI/AcxMainTextField";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import { useStore } from "utils/useStore";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";
import { observer } from "mobx-react";
import { formatHmsToSeconds } from "components/SoundClipEditor/utils";
import {
    ConversationComment,
    ConversationService,
} from "services/ConversationService";
import AcxMenu, {
    AcxMenuItemProps,
    StyledMenuLabel,
} from "components/UI/Menu/AcxMenu";
import useAllUserNames from "components/Signals/Dashboard/DashboardMenu/ShareDialog/useAllUsernames";
import { UserWithFullName } from "components/Signals/Dashboard/DashboardMenu/ShareDialog/constants";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import { useSearchParams } from "react-router-dom";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CommentText from "./CommentText";
import CommentList from "./CommentList";
import CommentComposer from "./CommentComposer";
import MentionDropdown from "./MentionDropdown";
import useMentions from "./useMentions";
import { applyMentionSelection } from "./mentionUtils";

const escapeRegExp = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getMentionTokenRegex = () => /@\[([^|\]]+)\|([^\]]+)\]/g;

const ConversationComments: React.FC = observer(() => {
    const store = useStore(ConversationsStore);
    const isChat = Boolean(store.selectedConversation?.isChat);
    const [comments, setComments] = React.useState<ConversationComment[]>([]);
    const [newComment, setNewComment] = React.useState("");
    const [isSaving, setIsSaving] = React.useState(false);
    const [activeReplyParentId, setActiveReplyParentId] = React.useState<
        string | null
    >(null);
    const [activeEditCommentId, setActiveEditCommentId] = React.useState<
        string | null
    >(null);
    const [commentMenuAnchorEl, setCommentMenuAnchorEl] =
        React.useState<HTMLButtonElement | null>(null);
    const [commentMenuId, setCommentMenuId] = React.useState<string | null>(
        null,
    );
    const [confirmDeleteCommentId, setConfirmDeleteCommentId] = React.useState<
        string | null
    >(null);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const commentInputRef = React.useRef<AcxMainTextFieldRef>(null);
    // Apply caret placement after controlled value updates.
    const pendingCursorPositionRef = React.useRef<number | null>(null);
    const playHeadTime = store.soundClipEditorStore?.playHeadTime ?? 0;
    const playHeadTimeRef = React.useRef(playHeadTime);
    const isAudioReady = Boolean(store.soundClipEditorStore?.clipAudioBuff);
    const isPeaksReady = Boolean(store.soundClipEditorStore?.isPeaksInit);
    const [commentTimestamp, setCommentTimestamp] = React.useState(
        durationDisplay(playHeadTime),
    );
    const [isTimestampEdited, setIsTimestampEdited] = React.useState(false);
    const [searchParams] = useSearchParams();
    const lastScrolledCommentIdRef = React.useRef<string | null>(null);

    const { users, isLoading: usersLoading } = useAllUserNames(
        store.authStore.orgStore.selectedOrganization!.id,
    );

    const {
        state: {
            matchingUsers: commentMatchingUsers,
            mentionSelections: commentMentionSelections,
            mentionStartIndex: commentMentionStartIndex,
            mentionDropdownPosition: commentMentionDropdownPosition,
            activeMentionIndex: commentActiveMentionIndex,
            shouldShowMentions: commentShouldShowMentions,
        },
        actions: {
            setMentionQuery: setCommentMentionQuery,
            setMentionStartIndex: setCommentMentionStartIndex,
            setMentionSelections: setCommentMentionSelections,
            setActiveMentionIndex: setCommentActiveMentionIndex,
            setCursorPosition: setCommentCursorPosition,
            syncCursorFromEvent: syncCommentCursorFromEvent,
            handleMentionKeyDown: handleCommentMentionKeyDown,
        },
        refs: { mentionDropdownRef: commentMentionDropdownRef },
    } = useMentions({
        users,
        usersLoading,
        inputRef: commentInputRef,
        inputValue: newComment,
    });

    React.useEffect(() => {
        playHeadTimeRef.current = playHeadTime;
    }, [playHeadTime]);

    const currentUser = store.authStore.permStore.user;
    const currentUserName = [currentUser?.firstName, currentUser?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
    const currentUserId = currentUser?.id ?? "current-user";
    const isUltraUser = store.authStore.isUserUltra();

    const usersById = React.useMemo(() => {
        if (!users) return new Map<string, UserWithFullName>();
        return new Map(
            users
                .filter((user) => user.userId)
                .map((user) => [user.userId!, user]),
        );
    }, [users]);

    const detokenizeMentions = React.useCallback(
        (commentText: string) => {
            const tokenRegex = getMentionTokenRegex();
            return commentText.replace(
                tokenRegex,
                (_, userId: string, displayName: string) => {
                    const user = usersById.get(userId);
                    const resolvedName = user?.fullName || displayName;
                    return `@${resolvedName}`;
                },
            );
        },
        [usersById],
    );

    const tokenizeMentions = React.useCallback(
        (commentText: string, selections: UserWithFullName[]) => {
            let result = commentText;
            if (selections.length > 0) {
                selections.forEach((selection) => {
                    if (!selection.userId || !selection.fullName) return;
                    const escapedName = escapeRegExp(selection.fullName);
                    const mentionRegex = new RegExp(
                        `(^|[\\s([{"'])@${escapedName}(?=$|[\\s.,!?;:)"'\\]}])`,
                        "gi",
                    );
                    result = result.replace(
                        mentionRegex,
                        (_, prefix: string) =>
                            `${prefix}@[${selection.userId}|${selection.fullName}]`,
                    );
                });
            }
            return result;
        },
        [],
    );

    const getTaggedUserIdsFromComment = React.useCallback(
        (commentText: string) => {
            const taggedUserIds = new Set<string>();
            const tokenRegex = getMentionTokenRegex();
            for (const match of commentText.matchAll(tokenRegex)) {
                if (match[1]) taggedUserIds.add(match[1]);
            }
            return Array.from(taggedUserIds);
        },
        [],
    );

    const handleAddComment = async () => {
        const trimmed = newComment.trim();
        if (!trimmed) return;

        if (!store.selectedConversationId) return;
        if (isSaving) return;

        const parsedTimestamp = formatHmsToSeconds(commentTimestamp);
        const commentTimestampSeconds = Number.isNaN(parsedTimestamp)
            ? playHeadTime
            : parsedTimestamp;
        const tokenizedComment = tokenizeMentions(
            newComment,
            commentMentionSelections,
        );
        const resolvedTaggedUserIds =
            getTaggedUserIdsFromComment(tokenizedComment);

        setIsSaving(true);
        try {
            const conversationService = new ConversationService();
            const commentId =
                await conversationService.createConversationComment({
                    audioMetadataId: store.selectedConversationId,
                    comment: tokenizedComment.trim(),
                    audioTimeStamp: commentTimestampSeconds,
                    parentCommentId: undefined,
                    taggedUsers: resolvedTaggedUserIds,
                });

            const nextComment: ConversationComment = {
                id: commentId,
                createdOn: new Date().toISOString(),
                creatorUserId: currentUserId,
                creatorUserName: currentUserName || "Unknown",
                audioMetadataId: store.selectedConversationId,
                comment: tokenizedComment.trim(),
                commentAudioTimestamp: commentTimestampSeconds,
                taggedUserIds: resolvedTaggedUserIds,
                reactions: [],
            };

            setComments((prev) => [nextComment, ...prev]);
            setNewComment("");
            setCommentTimestamp(durationDisplay(playHeadTime));
            setIsTimestampEdited(false);
            setCommentMentionSelections([]);
            setCommentMentionQuery("");
            setCommentMentionStartIndex(null);
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddReply = React.useCallback(
        async (
            parentComment: ConversationComment,
            commentText: string,
            taggedUserIds: string[],
        ) => {
            if (!store.selectedConversationId) return;
            if (!parentComment.id) return;
            if (!commentText.trim()) return;

            const replyTimestamp = !isChat
                ? parentComment.commentAudioTimestamp ?? playHeadTime
                : undefined;

            const conversationService = new ConversationService();
            const commentId =
                await conversationService.createConversationComment({
                    audioMetadataId: store.selectedConversationId,
                    comment: commentText.trim(),
                    audioTimeStamp: replyTimestamp,
                    parentCommentId: parentComment.id,
                    taggedUsers: taggedUserIds,
                });

            const nextReply: ConversationComment = {
                id: commentId,
                createdOn: new Date().toISOString(),
                creatorUserId: currentUserId,
                creatorUserName: currentUserName || "Unknown",
                audioMetadataId: store.selectedConversationId,
                parentCommentId: parentComment.id,
                comment: commentText.trim(),
                commentAudioTimestamp: replyTimestamp,
                taggedUserIds,
                reactions: [],
            };

            setComments((prev) => [nextReply, ...prev]);
        },
        [
            currentUserId,
            currentUserName,
            isChat,
            playHeadTime,
            store.selectedConversationId,
        ],
    );

    const handleCancel = React.useCallback(() => {
        setNewComment("");
        setCommentTimestamp(durationDisplay(playHeadTime));
        setIsTimestampEdited(false);
        setCommentMentionSelections([]);
        setCommentMentionQuery("");
        setCommentMentionStartIndex(null);
    }, [
        playHeadTime,
        setCommentMentionQuery,
        setCommentMentionSelections,
        setCommentMentionStartIndex,
        setCommentTimestamp,
        setIsTimestampEdited,
        setNewComment,
    ]);

    const handleReplyClick = React.useCallback(
        (commentId: string) => {
            setActiveReplyParentId(commentId);
        },
        [setActiveReplyParentId],
    );

    const handleCancelReply = React.useCallback(() => {
        setActiveReplyParentId(null);
    }, [setActiveReplyParentId]);

    const handleCancelEdit = React.useCallback(() => {
        setActiveEditCommentId(null);
    }, [setActiveEditCommentId]);

    const handleOpenCommentMenu = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>, commentId: string) => {
            setCommentMenuAnchorEl(event.currentTarget);
            setCommentMenuId(commentId);
        },
        [],
    );

    const handleCloseCommentMenu = React.useCallback(() => {
        setCommentMenuAnchorEl(null);
        setCommentMenuId(null);
    }, []);

    const formatCreatedAt = React.useCallback((timestamp: string) => {
        // Ensure UTC timestamps without offset are treated as UTC, then display locally.
        const normalizedTimestamp = /z$|[+-]\d{2}:\d{2}$/i.test(timestamp)
            ? timestamp
            : `${timestamp}Z`;
        const date = new Date(normalizedTimestamp);
        if (Number.isNaN(date.getTime())) return "";
        const datePart = date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
        const timePart = date.toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
        });
        return `${datePart} at ${timePart.toLowerCase()}`;
    }, []);

    const getTimestampMs = React.useCallback((timestamp?: string) => {
        if (!timestamp) return null;
        const normalizedTimestamp = /z$|[+-]\d{2}:\d{2}$/i.test(timestamp)
            ? timestamp
            : `${timestamp}Z`;
        const parsed = new Date(normalizedTimestamp);
        if (Number.isNaN(parsed.getTime())) return null;
        return parsed.getTime();
    }, []);

    const isCommentEdited = React.useCallback(
        (comment: ConversationComment) => {
            const modifiedMs = getTimestampMs(comment.modifiedOn);
            const createdMs = getTimestampMs(comment.createdOn);
            if (modifiedMs === null || createdMs === null) return false;
            return modifiedMs - createdMs > 1000;
        },
        [getTimestampMs],
    );

    const handleTimestampClick = React.useCallback(
        (timestamp: string) => {
            const timeInSeconds = formatHmsToSeconds(timestamp);
            if (Number.isNaN(timeInSeconds)) return;
            store.soundClipEditorStore?.setPlayHeadTime(timeInSeconds, true);
        },
        [store.soundClipEditorStore],
    );

    const canManageComment = React.useCallback(
        (comment: ConversationComment) =>
            Boolean(comment.creatorUserId === currentUserId || isUltraUser),
        [currentUserId, isUltraUser],
    );

    const startEditingComment = React.useCallback(
        (comment: ConversationComment) => {
            if (!comment.id) return;
            setActiveEditCommentId(comment.id);
        },
        [],
    );

    const handleSaveEdit = React.useCallback(
        async (
            commentId: string,
            commentText: string,
            taggedUserIds: string[],
        ) => {
            if (!commentId) return;
            if (!commentText.trim()) return;

            const conversationService = new ConversationService();
            await conversationService.updateConversationComment({
                commentId,
                comment: commentText.trim(),
                taggedUsers: taggedUserIds,
            });

            setComments((prev) =>
                prev.map((existing) =>
                    existing.id === commentId
                        ? {
                              ...existing,
                              comment: commentText.trim(),
                              taggedUserIds,
                              modifiedOn: new Date().toISOString(),
                          }
                        : existing,
                ),
            );
            handleCancelEdit();
        },
        [handleCancelEdit],
    );

    const handleDeleteComment = async (commentId: string) => {
        if (isDeleting) return;
        setIsDeleting(true);
        try {
            const conversationService = new ConversationService();
            await conversationService.deleteConversationComment(commentId);

            let idsToRemove = new Set<string>();
            setComments((prev) => {
                idsToRemove = new Set([commentId]);
                prev.forEach((comment) => {
                    if (comment.parentCommentId === commentId && comment.id) {
                        idsToRemove.add(comment.id);
                    }
                });

                return prev.filter(
                    (comment) => !comment.id || !idsToRemove.has(comment.id),
                );
            });
            if (activeReplyParentId && idsToRemove.has(activeReplyParentId)) {
                setActiveReplyParentId(null);
            }
            if (activeEditCommentId && idsToRemove.has(activeEditCommentId)) {
                handleCancelEdit();
            }
        } finally {
            setIsDeleting(false);
            setConfirmDeleteCommentId(null);
        }
    };

    const renderCommentText = React.useCallback(
        (text: string) => <CommentText text={text} usersById={usersById} />,
        [usersById],
    );

    const handleSelectMentionUser = React.useCallback(
        (user: UserWithFullName) => {
            applyMentionSelection(user, {
                value: newComment,
                mentionStartIndex: commentMentionStartIndex,
                setValue: setNewComment,
                setCursorPosition: setCommentCursorPosition,
                pendingCursorPositionRef,
                setMentionQuery: setCommentMentionQuery,
                setMentionStartIndex: setCommentMentionStartIndex,
                setMentionSelections: setCommentMentionSelections,
            });
        },
        [
            commentMentionStartIndex,
            newComment,
            setCommentCursorPosition,
            setCommentMentionQuery,
            setCommentMentionSelections,
            setCommentMentionStartIndex,
        ],
    );

    React.useEffect(() => {
        const amdId = store.selectedConversationId;
        setComments([]);
        setNewComment("");
        setIsSaving(false);
        setActiveReplyParentId(null);
        setActiveEditCommentId(null);
        setCommentMenuAnchorEl(null);
        setCommentMenuId(null);
        setConfirmDeleteCommentId(null);
        setIsDeleting(false);
        setIsLoading(false);
        setCommentTimestamp(durationDisplay(playHeadTimeRef.current));
        setIsTimestampEdited(false);
        setCommentMentionSelections([]);
        setCommentMentionQuery("");
        setCommentMentionStartIndex(null);
        setCommentActiveMentionIndex(0);
        setCommentCursorPosition(null);
        pendingCursorPositionRef.current = null;
        lastScrolledCommentIdRef.current = null;
        if (!amdId) return;

        let isMounted = true;
        const fetchComments = async () => {
            setIsLoading(true);
            try {
                const conversationService = new ConversationService();
                const result =
                    await conversationService.getConversationComments(amdId);
                if (!isMounted) return;
                const normalizeComment = (
                    comment: ConversationComment,
                ): ConversationComment => ({
                    ...comment,
                    id: comment.id ?? "",
                    creatorUserId: comment.creatorUserId,
                    audioMetadataId: comment.audioMetadataId,
                    parentCommentId: comment.parentCommentId,
                    taggedUserIds: comment.taggedUserIds ?? [],
                    reactions: comment.reactions ?? [],
                    childrenComments: comment.childrenComments ?? [],
                });

                const flattenComments = (
                    comment: ConversationComment,
                ): ConversationComment[] => {
                    const normalized = normalizeComment(comment);
                    const children = normalized.childrenComments ?? [];
                    return [
                        normalized,
                        ...children.flatMap((child) =>
                            flattenComments({
                                ...child,
                                parentCommentId:
                                    child.parentCommentId ?? normalized.id,
                            }),
                        ),
                    ];
                };

                setComments(result.flatMap(flattenComments));
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchComments();

        return () => {
            isMounted = false;
        };
    }, [
        store.selectedConversationId,
        setCommentActiveMentionIndex,
        setCommentCursorPosition,
        setCommentMentionQuery,
        setCommentMentionSelections,
        setCommentMentionStartIndex,
    ]);

    React.useEffect(() => {
        if (!isTimestampEdited) {
            setCommentTimestamp(durationDisplay(playHeadTime));
        }
    }, [isTimestampEdited, playHeadTime]);

    React.useEffect(() => {
        const pendingCursorPosition = pendingCursorPositionRef.current;
        if (pendingCursorPosition === null) return;
        const timer = window.setTimeout(() => {
            const input = commentInputRef.current;
            if (!input) return;
            input.focus?.();
            if ("setSelectionRange" in input) {
                input.setSelectionRange(
                    pendingCursorPosition,
                    pendingCursorPosition,
                );
            }
            pendingCursorPositionRef.current = null;
        }, 0);
        return () => window.clearTimeout(timer);
    }, [newComment]);

    React.useEffect(() => {
        const commentIdParam = searchParams.get("commentId");
        if (!commentIdParam) {
            lastScrolledCommentIdRef.current = null;
            return;
        }
        if (lastScrolledCommentIdRef.current === commentIdParam) return;
        if (isLoading || comments.length === 0) return;
        if (!isChat && (!isAudioReady || !isPeaksReady)) return;

        window.requestAnimationFrame(() => {
            const target = document.getElementById(`comment-${commentIdParam}`);
            if (!target) return;
            lastScrolledCommentIdRef.current = commentIdParam;
            const commentMatch = comments.find(
                (comment) => comment.id === commentIdParam,
            );
            const commentTimestamp = commentMatch?.commentAudioTimestamp;
            if (
                !isChat &&
                commentTimestamp !== undefined &&
                !Number.isNaN(commentTimestamp)
            ) {
                store.soundClipEditorStore?.setPlayHeadTime(
                    commentTimestamp,
                    true,
                );
            }
            target.scrollIntoView({ behavior: "smooth", block: "center" });
        });
    }, [
        comments,
        isLoading,
        isAudioReady,
        isChat,
        isPeaksReady,
        searchParams,
        store.soundClipEditorStore,
    ]);

    const mentionListboxId = "comment-mention-listbox";
    const getMentionOptionId = React.useCallback(
        (user: UserWithFullName, index: number) =>
            `comment-mention-option-${user.userId ?? index}`,
        [],
    );
    const { rootComments, repliesByParentId } = React.useMemo(() => {
        const getCreatedOnValue = (comment: ConversationComment) => {
            const createdOn = comment.createdOn ?? "";
            const normalizedCreatedOn = /z$|[+-]\d{2}:\d{2}$/i.test(createdOn)
                ? createdOn
                : `${createdOn}Z`;
            const value = new Date(normalizedCreatedOn).getTime();
            return Number.isNaN(value) ? 0 : value;
        };

        const rootComments: ConversationComment[] = [];
        const repliesByParentId = new Map<string, ConversationComment[]>();

        comments.forEach((comment) => {
            const parentId = comment.parentCommentId;
            if (!parentId) {
                rootComments.push(comment);
                return;
            }

            const replies = repliesByParentId.get(parentId) ?? [];
            replies.push(comment);
            repliesByParentId.set(parentId, replies);
        });

        repliesByParentId.forEach((replies) => {
            replies.sort(
                (left, right) =>
                    getCreatedOnValue(left) - getCreatedOnValue(right),
            );
        });

        return { rootComments, repliesByParentId };
    }, [comments]);

    const replyEditByParentId = React.useMemo(() => {
        const map = new Map<string, ConversationComment | null>();
        if (!activeEditCommentId) return map;
        comments.forEach((comment) => {
            if (comment.id === activeEditCommentId && comment.parentCommentId) {
                map.set(comment.parentCommentId, comment);
            }
        });
        return map;
    }, [activeEditCommentId, comments]);

    const commentMentionDropdown = (
        <MentionDropdown
            id={mentionListboxId}
            testId="comment-mention-dropdown"
            position={commentMentionDropdownPosition}
            isOpen={commentShouldShowMentions}
            users={commentMatchingUsers}
            activeIndex={commentActiveMentionIndex}
            dropdownRef={commentMentionDropdownRef}
            getOptionId={getMentionOptionId}
            onHover={setCommentActiveMentionIndex}
            onSelect={handleSelectMentionUser}
        />
    );

    const renderCommentContent = React.useCallback(
        (
            comment: ConversationComment,
            {
                showTimestamp,
                editComposer,
            }: { showTimestamp: boolean; editComposer?: React.ReactNode },
        ) => {
            const avatarSize = 32;
            const avatarFontSize = "13px";
            const nameFontSize = "14px";
            const dateFontSize = "12px";
            const textFontSize = "14px";
            const isEditing = Boolean(editComposer);
            const canManage = canManageComment(comment);

            return (
                <Stack spacing={0.5}>
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                        >
                            <Avatar
                                sx={{
                                    width: avatarSize,
                                    height: avatarSize,
                                    backgroundColor: theme.palette.primary[100],
                                    fontSize: avatarFontSize,
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                }}
                            >
                                {comment.creatorUserName?.charAt(0) ?? "?"}
                            </Avatar>
                            <Stack spacing={0}>
                                <Typography
                                    sx={{
                                        color: theme.palette.gray[800],
                                        fontSize: nameFontSize,
                                        fontWeight: 600,
                                    }}
                                >
                                    {comment.creatorUserName ?? "Unknown"}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme.palette.gray[500],
                                        fontSize: dateFontSize,
                                    }}
                                >
                                    {formatCreatedAt(comment.createdOn ?? "")}
                                    {isCommentEdited(comment) ? (
                                        <Box
                                            component="span"
                                            sx={{
                                                fontStyle: "italic",
                                                ml: 0.5,
                                            }}
                                        >
                                            Edited
                                        </Box>
                                    ) : null}
                                </Typography>
                            </Stack>
                        </Stack>
                        {comment.id && canManage && !isEditing ? (
                            <IconButton
                                aria-label="comment actions"
                                onClick={(event) =>
                                    handleOpenCommentMenu(event, comment.id!)
                                }
                                style={{
                                    color: theme.palette.black.main,
                                    height: "34px",
                                }}
                                size="small"
                            >
                                <MoreVertIcon />
                            </IconButton>
                        ) : null}
                    </Stack>
                    {isEditing ? (
                        editComposer
                    ) : (
                        <Typography
                            sx={{
                                color: theme.palette.gray[700],
                                fontSize: textFontSize,
                                lineHeight: "20px",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                            }}
                        >
                            <span>
                                {showTimestamp && (
                                    <>
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={() =>
                                                handleTimestampClick(
                                                    durationDisplay(
                                                        comment.commentAudioTimestamp ??
                                                            0,
                                                    ),
                                                )
                                            }
                                            sx={{
                                                border: "none",
                                                background: "none",
                                                padding: 0,
                                                margin: 0,
                                                color: theme.palette.primary
                                                    .main,
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                textDecoration: "underline",
                                                fontSize: textFontSize,
                                            }}
                                        >
                                            {durationDisplay(
                                                comment.commentAudioTimestamp ??
                                                    0,
                                            )}
                                        </Box>
                                        <> - </>
                                    </>
                                )}
                                {renderCommentText(comment.comment)}
                            </span>
                        </Typography>
                    )}
                </Stack>
            );
        },
        [
            canManageComment,
            formatCreatedAt,
            handleOpenCommentMenu,
            handleTimestampClick,
            isCommentEdited,
            renderCommentText,
        ],
    );

    const activeMenuComment = commentMenuId
        ? comments.find((comment) => comment.id === commentMenuId)
        : null;
    const commentMenuItems: AcxMenuItemProps[] = activeMenuComment
        ? [
              {
                  id: "conversation-comment-menu-edit",
                  label: <StyledMenuLabel>Edit</StyledMenuLabel>,
                  props: {
                      onClick: () => {
                          startEditingComment(activeMenuComment);
                          handleCloseCommentMenu();
                      },
                  },
              },
              {
                  id: "comment-delete",
                  label: <StyledMenuLabel>Delete</StyledMenuLabel>,
                  props: {
                      onClick: () => {
                          if (activeMenuComment.id) {
                              setConfirmDeleteCommentId(activeMenuComment.id);
                          }
                          handleCloseCommentMenu();
                      },
                  },
              },
          ]
        : [];

    const emptyCommentsState =
        !isLoading && rootComments.length === 0 ? (
            <Stack alignItems="center" py={5}>
                <AcxEmptyState
                    variant="new"
                    size="small"
                    overrideTitle="Start the Conversation!"
                    overrideSubTitle={`Leave a comment to share insights, ask questions, or tag a teammate to collaborate. Only people at ${store.authStore.orgStore.selectedOrganization?.name} can view your comments and notes.`}
                    containerMaxWidth="530px" //keep subtitle from filling
                />
            </Stack>
        ) : null;

    return (
        <Box>
            <AcxMenu
                anchorElement={commentMenuAnchorEl}
                menuItems={commentMenuItems}
                onMenuClose={handleCloseCommentMenu}
            />
            <AcxConfirmationDialog
                isOpen={Boolean(confirmDeleteCommentId)}
                title="Are you sure?"
                content="Deleting this comment is immediate and permanent. Are you sure you want to delete?"
                confirmButtonText="Delete comment"
                onClose={() => setConfirmDeleteCommentId(null)}
                onConfirm={() => {
                    if (confirmDeleteCommentId) {
                        handleDeleteComment(confirmDeleteCommentId);
                    }
                }}
                confirmButtonDisabled={isDeleting}
                confirmButtonId="comment-confirm-delete"
                headerIcon={
                    <WarningRoundedIcon color="error" fontSize="large" />
                }
            />
            {isLoading ? (
                <AcxEmptyState variant="loading" size="small" />
            ) : (
                <Stack spacing={2}>
                    <CommentComposer
                        isChat={isChat}
                        value={newComment}
                        isSaving={isSaving}
                        commentTimestamp={commentTimestamp}
                        inputRef={commentInputRef}
                        onFocus={() => {
                            setIsTimestampEdited(true);
                        }}
                        onChange={(event) => {
                            setNewComment(event.target.value);
                            syncCommentCursorFromEvent(event);
                        }}
                        inputProps={{
                            onClick: syncCommentCursorFromEvent,
                            onKeyUp: syncCommentCursorFromEvent,
                            onKeyDown: (event) =>
                                handleCommentMentionKeyDown(
                                    event,
                                    handleSelectMentionUser,
                                ),
                            onBlur: () => {
                                setCommentMentionQuery("");
                                setCommentMentionStartIndex(null);
                            },
                            role: "combobox",
                            "aria-autocomplete": "list",
                            "aria-expanded": commentShouldShowMentions,
                            "aria-controls": commentShouldShowMentions
                                ? mentionListboxId
                                : undefined,
                            "aria-activedescendant": commentShouldShowMentions
                                ? getMentionOptionId(
                                      commentMatchingUsers[
                                          commentActiveMentionIndex
                                      ],
                                      commentActiveMentionIndex,
                                  )
                                : undefined,
                        }}
                        onTimestampChange={(value) => {
                            setCommentTimestamp(value);
                            setIsTimestampEdited(true);
                        }}
                        onCancel={handleCancel}
                        onSubmit={handleAddComment}
                        mentionDropdown={commentMentionDropdown}
                    />
                    <Stack spacing={2}>
                        {emptyCommentsState}
                        <CommentList
                            rootComments={rootComments}
                            repliesByParentId={repliesByParentId}
                            replyEditByParentId={replyEditByParentId}
                            isChat={isChat}
                            activeReplyParentId={activeReplyParentId}
                            activeEditCommentId={activeEditCommentId}
                            renderCommentContent={renderCommentContent}
                            users={users}
                            usersLoading={usersLoading}
                            tokenizeMentions={tokenizeMentions}
                            detokenizeMentions={detokenizeMentions}
                            getTaggedUserIdsFromComment={
                                getTaggedUserIdsFromComment
                            }
                            onAddReply={handleAddReply}
                            onSaveEdit={handleSaveEdit}
                            onCancelEdit={handleCancelEdit}
                            onReplyClick={handleReplyClick}
                            onCancelReply={handleCancelReply}
                        />
                    </Stack>
                </Stack>
            )}
        </Box>
    );
});

export default ConversationComments;
